import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {conjugationSiteKey} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

function compareConjugationSites(a: ConjugationSite, b: ConjugationSite): number {
  if (a.domainLabel !== b.domainLabel) {
    return a.domainLabel < b.domainLabel ? -1 : 1;
  }
  return a.residueIndex - b.residueIndex;
}

/**
 * Serializes sites to the text stored in a Conjugation sites cell: a JSON array of `ConjugationSite`s, sorted
 * and de-duplicated so the stored value is stable. No sites serialize to an empty cell.
 */
export function serializeConjugationSites(sites: Array<ConjugationSite>): string {
  if (sites.length === 0) {
    return '';
  }
  const sitesByKey = new Map(
    sites.map(({domainLabel, residueIndex}) => [
      conjugationSiteKey({domainLabel, residueIndex}),
      {domainLabel, residueIndex},
    ])
  );
  return JSON.stringify([...sitesByKey.values()].sort(compareConjugationSites));
}

function isConjugationSite(value: unknown): value is ConjugationSite {
  if (typeof value !== 'object' || value == null) {
    return false;
  }
  const {domainLabel, residueIndex} = value as Partial<Record<keyof ConjugationSite, unknown>>;
  return (
    typeof domainLabel === 'string' &&
    domainLabel !== '' &&
    typeof residueIndex === 'number' &&
    Number.isInteger(residueIndex) &&
    residueIndex >= 0
  );
}

/**
 * Parses the text stored in a Conjugation sites cell. Returns an empty list for an empty cell, and null when
 * the text is not a serialized list of sites (e.g. hand-typed or pasted text).
 */
export function parseConjugationSites(text: string): Array<ConjugationSite> | null {
  if (text.trim() === '') {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }
  if (!Array.isArray(parsed) || !parsed.every((value) => isConjugationSite(value))) {
    return null;
  }
  return parsed.map(({domainLabel, residueIndex}) => ({domainLabel, residueIndex}));
}

/** Parses a Conjugation sites cell's typed value; see {@link parseConjugationSites}. */
export function parseConjugationSitesTypedValue(
  typedValue: TypedValue | null | undefined
): Array<ConjugationSite> | null {
  if (typedValue == null) {
    return [];
  }
  return typedValue.type === BaseTypedValueType.STRING ? parseConjugationSites(typedValue.rawValue) : null;
}
